import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DataTable } from "@/components/app/DataTable";
import { ImportDialog } from "@/components/app/ImportDialog";
import { LmTypes, MovementTypes } from "@/components/config/TypeMasters";
import { SupportingLists } from "@/components/config/SupportingLists";
import { ItemMigration, ItemSeries } from "@/components/admin/ItemAdmin";
import { LegacyLmSwitch } from "@/components/order/LegacyLmUpload";
import { useFetch } from "@/hooks/useFetch";
import { reloadLookups } from "@/hooks/useLookups";
import { api, errMsg } from "@/lib/api";
import { label } from "@/lib/format";

function Numbering() {
  const { data, reload } = useFetch("/admin/sequences");
  const setLm = async () => {
    const n = window.prompt("Next LM number (shared by all LM types)?");
    if (!n) return;
    const reason = window.prompt("Reason?");
    if (!reason) return;
    try { await api.post("/lm-types/next-number", { next_number: Number(n), reason }); toast.success("Next LM number set"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const cols = [{ key: "name", label: "Sequence" }, { key: "last_used", label: "Last used", right: true }, { key: "next", label: "Next", right: true },
    { key: "act", label: "", exp: false, render: (r) => r.key === "lm" && <button className="text-sky-700 hover:underline" onClick={setLm} data-testid="seq-lm-set">Set next</button> }];
  return (
    <div className="space-y-3" data-testid="admin-numbering">
      <DataTable testid="sequences-table" columns={cols} rows={data} rowId={(r) => r.key} />
      <p className="text-xs text-slate-500">LM prefix and number format are set per LM type (LM types tab). Other numbers run automatically and can't be reset, so references stay unique.</p>
    </div>
  );
}

function Statuses() {
  const { data } = useFetch("/admin/statuses");
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="admin-statuses">
      {(data || []).map((s) => (
        <div key={s.list} className="rounded-md border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-1 text-sm font-semibold"><Lock className="h-3 w-3 text-slate-400" />{s.list}</div>
          <div className="text-xs text-slate-500 mb-2">{s.note}</div>
          <div className="flex flex-wrap gap-1">{s.values.map((v) => <span key={v} className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-700">{label(v)}</span>)}</div>
        </div>))}
      <p className="text-xs text-slate-500 md:col-span-2 xl:col-span-3">These statuses drive the workflow (approvals, dispatch, POD) so they are fixed. Business dropdowns are editable under Supporting lists.</p>
    </div>
  );
}

const TABS = [["lm-types", "LM types", LmTypes], ["movement-types", "Movement types", MovementTypes], ["numbering", "Numbering", Numbering], ["item-series", "Item code series", ItemSeries],
  ["lists", "Supporting lists", SupportingLists], ["doc-categories", "Document categories", () => <SupportingLists only="document_category" />], ["statuses", "Statuses", Statuses]];

export function ConfigMasters() {
  const [sp, setSp] = useSearchParams();
  return (
    <Tabs value={sp.get("tab") || "lm-types"} onValueChange={(v) => setSp({ tab: v })} data-testid="admin-config">
      <TabsList className="mb-3 flex-wrap h-auto">{TABS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`config-tab-${k}`}>{l}</TabsTrigger>)}</TabsList>
      {TABS.map(([k, , C]) => <TabsContent key={k} value={k}><C /></TabsContent>)}
    </Tabs>
  );
}

const LINKS = [["Customers Master — import + HQ update", "/masters/customers"], ["Item Master — CSV import", "/masters/items"], ["Transporters — import", "/masters/other"], ["Asset import", "/assets/import"]];

function DataHealth() {
  const { data } = useFetch("/lookups/health");
  const cols = [{ key: "list", label: "List" }, { key: "value", label: "Value on records", mono: true }, { key: "collection", label: "Where" }, { key: "records", label: "Records", right: true }];
  return (
    <div data-testid="lookup-health">
      <h3 className="text-sm font-semibold mt-6 mb-1">Data health — values not in an active list</h3>
      <p className="text-xs text-slate-500 mb-2">Records keep their historical value. Add the value (or an alias) to the list, or correct the record.</p>
      <DataTable testid="lookup-health-table" columns={cols} rows={data} rowId={(r) => `${r.type}-${r.collection}-${r.value}`} exportName="Data_health" empty="All stored values match an active list value" />
    </div>
  );
}

export function Utilities() {
  const [imp, setImp] = useState(false);
  return (
    <div className="mt-8" data-testid="admin-utilities">
      <h3 className="text-sm font-semibold mb-2">Imports</h3>
      <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-3">
        {LINKS.map(([l, to]) => <Link key={to} to={to} className="rounded-md border border-slate-200 bg-white p-4 text-sm hover:border-sky-600 transition-colors" data-testid={`admin-link-${to.replace(/[^a-z]+/g, "-")}`}>{l} →</Link>)}
        <button onClick={() => setImp(true)} className="rounded-md border border-dashed border-slate-300 bg-white p-4 text-left text-sm hover:border-sky-600 transition-colors" data-testid="admin-import-lookups-btn">Supporting-list values from Excel →</button>
      </div>
      <ImportDialog entity="lookups" title="Import supporting-list values" open={imp} onOpenChange={setImp} onDone={reloadLookups} />
      <DataHealth />
      <LegacyLmSwitch />
      <ItemMigration />
    </div>
  );
}
